/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SystemVarsFieldRenderer.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System Vars module.
----------------------------------------------------------------*/


import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { CodecField } from '../../../components/ui/CodecField';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import { SystemVar } from '../types/systemVars.types';

const SELECT_CLS = 'px-3 py-2 text-sm border border-border rounded bg-background text-foreground w-full';
const TLS_VERSIONS = ['sslv23','tlsv1','tlsv1.1','tlsv1.2','tlsv1.3','tlsv1.2,tlsv1.3','tlsv1.3,tlsv1.2'];
const TLS_CIPHERS = [
    'ALL:!ADH:!LOW:!EXP:!MD5:@STRENGTH','HIGH:!aNULL:!MD5',
    'ECDHE+AESGCM:ECDHE+CHACHA20:DHE+AESGCM:DHE+CHACHA20:!aNULL:!MD5:!DSS',
    'ECDHE+AESGCM:ECDHE+CHACHA20:DHE+AESGCM:DHE+CHACHA20',
    'HIGH:!aNULL:!MD5:!RC4','ALL:!aNULL:!eNULL:!EXPORT:!DES:!RC4:!MD5:!PSK:!SRP:!CAMELLIA',
];

interface Props {
    setting: SystemVar;
    currentValue: string;
    onFieldChange: (name: string, value: string) => void;
}

function SimpleSelect({ value, options, onChange }: { value: string; options: string[]; onChange: (v: string) => void }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} className={SELECT_CLS}>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

export function SystemVarsFieldRenderer({ setting, currentValue, onFieldChange }: Props) {
    const meta = setting.settingMetadata;
    const varName = meta.variable_name;
    const possibleRaw = meta.possible_values;
    const possible = possibleRaw?.toLowerCase();

    const change = (v: string) => onFieldChange(varName, v);

    // Language selector
    if (varName === 'language') {
        return (
            <LanguageSelector
                value={currentValue}
                onChange={(v) => {
                    const lang = v.includes('-') && v.split('-').length > 2 ? v.split('-').slice(0, -1).join('-') : v;
                    change(lang);
                }}
                showVoiceSelector={false} showLabel={false}
            />
        );
    }

    // TLS-specific selects
    if (varName === 'sip_tls_version') return <SimpleSelect value={currentValue} options={TLS_VERSIONS} onChange={change} />;
    if (varName === 'sip_tls_ciphers') return <SimpleSelect value={currentValue} options={TLS_CIPHERS} onChange={change} />;

    // Boolean toggle
    if (['boolean','true / false','true, false','yes, no','yes / no'].includes(possible ?? '')) {
        return <Toggle value={currentValue === 'true'} onChange={(v) => change(v ? 'true' : 'false')} />;
    }

    // Codec list
    if (possible === 'codec list' || possible === 'codec' || possible === 'comma-separated list' || varName?.includes('codec')) {
        return <CodecField value={currentValue} onChange={change} allowVideo={false} />;
    }

    // Number input
    if (possible?.includes('integer') || possible?.includes('number')) {
        return <Input type="number" value={currentValue} onChange={(e) => change(e.target.value)} placeholder={meta.default_value} className="text-sm" />;
    }

    // Comma-separated select options
    if (possible?.includes(',') && !possible.includes('true') && !possible.includes('false') || varName === 'rtp_secure_media') {
        const opts = (possibleRaw || '').split(',').map((o) => o.trim()).filter(Boolean);
        return <SimpleSelect value={currentValue} options={opts} onChange={change} />;
    }

    // Space-separated suites
    if (possible?.includes('space-separated') || possible?.includes('suites')) {
        const opts = (meta.default_value || '').split(' ').filter((o) => o.trim());
        if (opts.length > 1) return <SimpleSelect value={currentValue} options={opts} onChange={change} />;
    }

    // Colon-separated suites
    if (possible?.includes('list of suites')) {
        const opts = (meta.default_value || '').split(':').filter((o) => o.trim());
        if (opts.length > 1) return <SimpleSelect value={currentValue} options={opts} onChange={change} />;
    }

    // Default text input
    return <Input value={currentValue} onChange={(e) => change(e.target.value)} placeholder={meta.default_value} className="text-sm" />;
}
